<template>
  <div class="max-w-7xl mx-auto px-4 w-full">
    <div class="grid items-center gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(240px,0.9fr)] lg:gap-10">
      <div
        class="text-center md:text-left select-none"
        :class="[
          variant === 'gradient' ? 'max-w-2xl' : 'max-w-xl',
          'mx-auto md:mx-0',
        ]"
      >
        <component
          :is="isPrimary ? 'h1' : 'h2'"
          :class="titleClass"
          :style="titleStyle"
        >
          {{ banner.title }}
        </component>
        <p
          v-if="banner.subtitle"
          :class="[subtitleClass, 'mx-auto md:mx-0']"
          :style="subtitleStyle"
        >
          {{ banner.subtitle }}
        </p>
        <div v-if="banner.buttons?.length" class="flex flex-wrap gap-3 justify-center md:justify-start">
          <UButton
            v-for="(button, index) in banner.buttons"
            :key="`${button.label}-${index}`"
            :label="button.label"
            :to="button.to ? shopPath(button.to) : undefined"
            :variant="resolvedVariant(button)"
            :color="resolvedColor(button)"
            :size="button.size || 'lg'"
            :class="outlineButtonClass(button)"
          />
        </div>
      </div>
      <div
        v-if="banner.lottie"
        :class="variant === 'image' ? 'hidden lg:flex justify-end' : 'flex justify-center lg:justify-end'"
      >
        <HeroLottie :key="lottieKey" :data="banner.lottie" />
      </div>
      <div v-else-if="variant === 'image'" class="hidden lg:block" aria-hidden="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import type { Banner, BannerButton } from '~/types'

const HeroLottie = defineAsyncComponent(() => import('~/components/HeroLottie.client.vue'))
const shopPath = useShopPath()

const props = withDefaults(defineProps<{
  banner: Banner
  variant: 'image' | 'gradient'
  isPrimary?: boolean
  overlay?: boolean
}>(), {
  isPrimary: false,
  overlay: true,
})

const lottieKey = computed(() => `${props.banner.id}-${JSON.stringify(props.banner.lottie ?? {}).length}`)

const titleStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.banner.titleColor) style.color = props.banner.titleColor
  if (props.banner.titleFontFamily) style.fontFamily = props.banner.titleFontFamily
  return Object.keys(style).length ? style : undefined
})

const subtitleStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.banner.subtitleColor) style.color = props.banner.subtitleColor
  if (props.banner.subtitleFontFamily) style.fontFamily = props.banner.subtitleFontFamily
  return Object.keys(style).length ? style : undefined
})

const titleClass = computed(() => {
  const colorClass = props.banner.titleColor
    ? ''
    : (props.variant === 'gradient' || props.overlay ? 'text-white' : 'text-gray-900')
  return ['text-2xl md:text-4xl font-bold mb-2 md:mb-4', colorClass].filter(Boolean).join(' ')
})

const subtitleClass = computed(() => {
  let colorClass = 'text-gray-700'
  if (!props.banner.subtitleColor) {
    if (props.variant === 'gradient') colorClass = 'text-brand-100'
    else if (props.overlay) colorClass = 'text-gray-200'
  } else {
    colorClass = ''
  }

  const base = props.variant === 'gradient'
    ? 'text-lg md:text-xl mb-8'
    : 'text-lg mb-4 max-w-lg'

  return [base, colorClass].filter(Boolean).join(' ')
})

function resolvedColor(button: BannerButton): NonNullable<BannerButton['color']> {
  if (props.variant === 'image' && !props.overlay && button.color === 'white') {
    return 'primary'
  }
  return button.color || 'primary'
}

function resolvedVariant(button: BannerButton): NonNullable<BannerButton['variant']> {
  // Storefront uses flat (solid) buttons; map legacy outline to solid.
  if (button.variant === 'outline') return 'solid'
  return button.variant || 'solid'
}

function outlineButtonClass(button: BannerButton) {
  if (props.overlay && button.variant === 'outline' && button.color === 'white') {
    return 'text-white'
  }
  return undefined
}
</script>
